import React, { FC, useMemo, useState } from 'react';
import { EndOfSeasonSnapshots, getSnapshotData, Snapshot } from '../../scripts/dataSources/snapshots';
import { DivisionID, SubleagueID, TeamID } from '../../scripts/models/common';
import { TeamModel } from '../../scripts/models/models';
import LoadingPage from '../common/LoadingPage';
import ModeSelector from '../common/modeSelector/ModeSelector';
import { DataContext } from '../context/dataLoader/DataLoader';
import { ILine } from './teamBarChart/BarLines';
import TeamBarChart from './teamBarChart/TeamBarChart';

import './TeamWinsCharts.scss';

const ALL_MODES = ["All Teams", "Subleagues"] as const;
type mode = (typeof ALL_MODES)[number];

function wins(team: TeamModel) {
    let standings = team.standings[0] ?? team.standings;
    return standings.wins;
}

interface ITeamChartInfo {
    teamSet: TeamModel[],
    data: Record<TeamID, number>,
    title: string,
    key: string,
}

const TeamWinsCharts: FC = () => {


    const [currentMode, setCurrentMode] = useState<mode>("Subleagues")
    const { teamCollections, simModel, snapshots } = React.useContext(DataContext)

    const snapMap = useMemo(() => {
        let map: Record<string, Snapshot | undefined> = {};
        EndOfSeasonSnapshots.forEach((snapshot) => {
            map[snapshot.name] = snapshot;
        });
        map["Current data"] = undefined;
        return map;
    }, [])

    const snapOptions = useMemo(() => {
        return EndOfSeasonSnapshots.map(snap => snap.name).concat("Current data")
    }, [])

    const [currentSnapName, setCurrentSnapName] = useState<string>("Current data")

    const currentSnapshot = snapMap[currentSnapName];


    if (!teamCollections || !simModel) {
        return <LoadingPage />
    }


    const snapshotData = getSnapshotData(currentSnapshot, snapshots);
    const teamsToUse = (snapshotData) ? snapshotData.teamCollections : teamCollections

    const precision = 5;
    const frac = (99 / (simModel.simData.currentDay + 1))
    const scale = Math.min(9, Math.floor(precision * frac) / precision)


    const extraLines: ILine[] = [];
    const midwayLine: ILine = {
        color: "white",
        position: simModel.simData.currentDay / 2
    }

    // Don't show the 50% line for the first 5 games or so, it's useless that soon
    if (simModel.simData.currentDay > 6) {
        extraLines.push(midwayLine)
    }



    const chartInfos: ITeamChartInfo[] = [];

    switch (currentMode) {
        case "All Teams":
            const teamSet = [...teamsToUse.allTeams];
            teamSet.sort((a, b) => wins(b) - wins(a));

            const dataSet: Record<TeamID, number> = {}
            teamSet.forEach(team => { dataSet[team.id] = wins(team) }) // TODO is this the right way to get wins?

            const title = "Season Wins - All Teams"
            chartInfos.push({
                teamSet,
                data: dataSet,
                title,
                key: "allTeams"
            })
            break;
        case "Subleagues":
            const subleagues = simModel.simData.currentLeagueData.subLeagues;
            const divisionsBySubleague: Record<SubleagueID, DivisionID[]> = {}
            const teamsByDivision: Record<DivisionID, TeamModel[]> = {}
            subleagues.forEach(subleague => {
                divisionsBySubleague[subleague.id] = []
                subleague.divisions.forEach((division) => {
                    divisionsBySubleague[subleague.id].push(division.id);
                    teamsByDivision[division.id] = []
                })
            })
            teamsToUse.allTeams.forEach((team) => {
                teamsByDivision[team.division.id].push(team)
            })
            subleagues.forEach((subleague, idx) => {
                let teamSet: TeamModel[] = [];
                divisionsBySubleague[subleague.id].forEach((division) => {
                    teamSet = teamSet.concat(teamsByDivision[division])
                })

                teamSet.sort((a, b) => wins(b) - wins(a));

                const dataSet: Record<TeamID, number> = {}
                teamSet.forEach(team => { dataSet[team.id] = wins(team) }) // TODO is this the right way to get wins?

                const title = `Season Wins - The Conference of ${subleague.divisions[0].name.split(" ")[1]}`
                chartInfos.push({
                    teamSet,
                    data: dataSet,
                    title,
                    key: `subleague ${idx}`
                })
            })
            break;
    }





    return <div className='Page WinsViewer'>
        <div className="selector">
            <ModeSelector currentSelection={currentMode} options={ALL_MODES} setter={setCurrentMode} />
            {
                snapshots && <ModeSelector currentSelection={currentSnapName} setter={setCurrentSnapName} options={snapOptions} />
            }
        </div>
        <div className="charts serif">
            {
                chartInfos.map(info => (
                    <TeamBarChart key={info.key} teamSet={info.teamSet} data={info.data} title={info.title} scale={scale} extraLines={extraLines} />
                ))
            }
        </div>
    </div>
}

export default TeamWinsCharts;